// src/cockpit4/components/MultiTenantPanel.tsx
// ============================================
// 多租户与私有化（设置中心 P2-功能7）- v2.0 详细改进计划
// 租户隔离 / 租户管理计费 / 私有化部署支持 / 定制化 / 租户级配置 / 数据迁移
// 数据来源：localStorage（xeaos_multi_tenant）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Building2, ShieldCheck, Server, Database, Plus } from "lucide-react";

interface Tenant {
  id: string;
  name: string;
  plan: "企业版" | "专业版" | "试用版";
  users: number;
  storage: string;
  status: "正常" | "试用中" | "已到期";
  isolated: boolean;
  expires: string;
}

const INITIAL_TENANTS: Tenant[] = [
  { id: "t1", name: "XEAOS 主租户", plan: "企业版", users: 12, storage: "2.4GB", status: "正常", isolated: true, expires: "2027-08-01" },
  { id: "t2", name: "测试租户 A", plan: "试用版", users: 3, storage: "0.2GB", status: "试用中", isolated: true, expires: "2026-09-01" },
  { id: "t3", name: "客户演示环境", plan: "专业版", users: 5, storage: "0.8GB", status: "正常", isolated: true, expires: "2026-12-15" },
];

const KEY = "xeaos_multi_tenant";

function load(): Tenant[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as Tenant[];
  } catch { /* ignore */ }
  return INITIAL_TENANTS;
}

export default function MultiTenantPanel() {
  const [tenants, setTenants] = useState<Tenant[]>(load);

  const stats = useMemo(() => {
    const totalUsers = tenants.reduce((a, t) => a + t.users, 0);
    const active = tenants.filter((t) => t.status === "正常").length;
    const isolated = tenants.filter((t) => t.isolated).length;
    return { totalUsers, active, isolated };
  }, [tenants]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Building2 size={14} className="text-blue-400" />
        <h3 className="text-[12px] font-medium text-white">多租户与私有化</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能7 · Multi-Tenant</span>
        <button className="ml-auto flex items-center gap-1 px-2 py-1 rounded-md border border-blue-500/30 bg-blue-500/10 text-[10px] text-blue-300 hover:bg-blue-500/20">
          <Plus size={10} /> 新建租户
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="租户总数" value={`${tenants.length} 个`} color="#94A3B8" />
        <MetricCard label="活跃租户" value={`${stats.active} 个`} color="#34D399" />
        <MetricCard label="数据隔离" value={`${stats.isolated}/${tenants.length}`} color="#38BDF8" />
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={11} className="text-emerald-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">租户管理 · 数据/配置/资源隔离</span>
        </div>
        <div className="space-y-1.5">
          {tenants.map((t) => (
            <div key={t.id} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium text-white">{t.name}</span>
                <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300">{t.plan}</span>
                <span className={`ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded ${
                  t.status === "正常" ? "bg-emerald-500/15 text-emerald-400" : t.status === "试用中" ? "bg-amber-500/15 text-amber-400" : "bg-red-500/15 text-red-400"
                }`}>{t.status}</span>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-[9px] font-mono text-gray-500">
                <span>{t.users} 用户</span>
                <span>存储 {t.storage}</span>
                <span className="flex items-center gap-0.5"><Database size={8} /> {t.isolated ? "数据隔离" : "共享"}</span>
                <span className="ml-auto">到期 {t.expires}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-[9px] font-mono">
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
          <div className="flex items-center gap-1.5 text-cyan-300 mb-1"><Server size={10} /> 私有化部署</div>
          <div className="text-gray-400">支持 Docker 一键部署 · 数据不出企业 · 版本独立控制 · 定制化主题与流程</div>
        </div>
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
          <div className="flex items-center gap-1.5 text-purple-300 mb-1"><Database size={10} /> 数据迁移工具</div>
          <div className="text-gray-400">租户间数据导出/导入 · 从私有化迁回云端 · 完整审计留痕</div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}
